import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { jsonLdScript, webPage } from '@/lib/schema'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowRight } from 'lucide-react'
import { BrowserCheckCta } from '@/components/browser-check-cta'

export const Route = createFileRoute('/_site/tools/sla-calculator')({
  component: SlaCalculatorPage,
  head: () => ({
    meta: [
      { title: 'SLA Calculator — Allowed Downtime per SLA' },
      { name: 'description', content: 'Calculate allowed downtime and error budget for any SLA target. See monthly, quarterly, and yearly budgets for 99%, 99.9%, 99.95%, 99.99%, and 99.999% SLAs.' },
      { property: 'og:title', content: 'SLA Calculator — Calculate Allowed Downtime for Your SLA' },
      { property: 'og:description', content: 'Calculate your SLA error budget. How much downtime does your SLA actually allow?' },
      { property: 'og:image', content: 'https://uptrack.app/og/tools-sla-calculator.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/tools/sla-calculator' },
    ],
    scripts: [
      jsonLdScript(webPage({
        name: 'SLA Calculator',
        description: 'Calculate allowed downtime and remaining error budget for any SLA percentage and monitoring period.',
        url: 'https://uptrack.app/tools/sla-calculator',
      })),
    ],
  }),
})

const periods = [
  { label: 'Monthly', days: 30.44 },
  { label: 'Quarterly', days: 91.31 },
  { label: 'Yearly', days: 365.25 },
] as const

const slaTiers = [99, 99.5, 99.9, 99.95, 99.99, 99.999]

function fmt(seconds: number): string {
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const parts: string[] = []
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m}m`)
  if (s > 0 || parts.length === 0) parts.push(`${s}s`)
  return parts.join(' ')
}

function SlaCalculatorPage() {
  const [sla, setSla] = useState(99.95)
  const [periodIdx, setPeriodIdx] = useState(0)
  const [usedMinutes, setUsedMinutes] = useState(0)

  const period = periods[periodIdx]
  const totalSeconds = period.days * 24 * 3600
  const allowedDowntime = ((100 - sla) / 100) * totalSeconds
  const usedSeconds = usedMinutes * 60
  const remaining = Math.max(0, allowedDowntime - usedSeconds)
  const budgetPct = allowedDowntime > 0 ? Math.min(100, (usedSeconds / allowedDowntime) * 100) : 100

  return (
    <>
      <>
        <section className="container mx-auto px-4 pb-8 pt-12 md:pt-16">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">SLA Calculator</h1>
            <p className="mt-4 max-w-2xl text-lead text-body">Calculate your allowed downtime and error budget for any service level agreement.</p>
          </div>
        </section>

        <section className="pb-12 md:pb-16">
          <div className="container mx-auto max-w-3xl px-4">
            <div className="rounded-lg border border-border bg-card p-5 md:p-6">
              <div className="mb-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="sla" className="mb-2 block text-sm font-medium">SLA target (%)</Label>
                  <Input id="sla" type="number" step="0.001" min="0" max="100" value={sla} onChange={(e) => setSla(parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <Label htmlFor="used" className="mb-2 block text-sm font-medium">Downtime used (minutes)</Label>
                  <Input id="used" type="number" min="0" step="1" value={usedMinutes} onChange={(e) => setUsedMinutes(parseFloat(e.target.value) || 0)} />
                </div>
              </div>
              <div className="mb-6 flex gap-2">
                {periods.map((p, i) => (
                  <Button key={p.label} variant={periodIdx === i ? 'default' : 'outline'} size="sm" onClick={() => setPeriodIdx(i)}>
                    {p.label}
                  </Button>
                ))}
              </div>

              <div className="mb-4 grid gap-4 sm:grid-cols-3">
                <div className="rounded-md border border-border bg-background p-4">
                  <p className="text-xs text-muted-foreground">Allowed downtime</p>
                  <p className="mt-1 num text-lg font-semibold text-foreground">{fmt(allowedDowntime)}</p>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <p className="text-xs text-muted-foreground">Budget remaining</p>
                  <p className={`mt-1 text-lg font-semibold ${remaining > 0 ? 'text-status-up' : 'text-status-down'}`}>{fmt(remaining)}</p>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <p className="text-xs text-muted-foreground">Budget used</p>
                  <p className={`mt-1 text-lg font-semibold ${budgetPct >= 100 ? 'text-status-down' : budgetPct >= 80 ? 'text-status-degraded' : 'text-status-up'}`}>{budgetPct.toFixed(1)}%</p>
                </div>
              </div>

              {budgetPct >= 100 && (
                <div className="mb-4 rounded-md border border-status-down/50 bg-status-down/10 p-3 text-sm text-status-down">
                  You have exceeded your SLA error budget. Any additional downtime puts you in breach of your SLA commitment.
                </div>
              )}
            </div>

            <div className="mt-8 rounded-lg border border-border bg-card p-5 md:p-6">
              <h2 className="mb-4 text-lg font-semibold">Common SLA tiers ({period.label.toLowerCase()} allowed downtime)</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-3 text-left font-medium text-muted-foreground">SLA</th>
                    <th className="pb-3 text-right font-medium text-muted-foreground">Allowed downtime</th>
                    <th className="pb-3 text-right font-medium text-muted-foreground">Uptime required</th>
                  </tr>
                </thead>
                <tbody>
                  {slaTiers.map((tier) => {
                    const dt = ((100 - tier) / 100) * totalSeconds
                    return (
                      <tr key={tier} className={`border-b border-border last:border-0 ${tier === sla ? 'bg-muted' : ''}`}>
                        <td className={`py-2 font-medium ${tier === sla ? 'text-primary' : ''}`}>{tier}%</td>
                        <td className="py-2 text-right num text-muted-foreground">{fmt(dt)}</td>
                        <td className="py-2 text-right num text-muted-foreground">{fmt(totalSeconds - dt)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-card">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Monitor your uptime with Uptrack</h2>
            <p className="mb-6 mt-3 max-w-xl text-body">
              Pro includes 50 monitors — track your SLA compliance in real time.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        <section className="container mx-auto max-w-3xl px-4 pb-16">
          <BrowserCheckCta />
        </section>
      </>
      </>
  )
}
